import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  initialPaginationState,
  mergeIds,
  PaginationState,
} from '@redux/helpers';
import { SHOWS_PER_PAGE } from '@utils/constants';

export type FeedSuggestedSubBlock = {
  myTeam: PaginationState;
  nonMyTeam: PaginationState;
};

export type FeedSuggestedBlock = {
  live: FeedSuggestedSubBlock;
  recorded: PaginationState;
};

export type FeedFollowingBlock = {
  live: PaginationState;
  recorded: PaginationState;
};

type InitialState = {
  suggested: FeedSuggestedBlock;
  following: FeedFollowingBlock;
  scheduled: PaginationState;
  myProfile: PaginationState;
  byProfileId: Record<string, PaginationState>;
};

export const initialState: InitialState = {
  suggested: {
    live: {
      myTeam: initialPaginationState,
      nonMyTeam: initialPaginationState,
    },
    recorded: initialPaginationState,
  },
  following: {
    live: { ids: [], mayLoadMore: true },
    recorded: { ids: [], mayLoadMore: true },
  },
  scheduled: { ids: [], mayLoadMore: true },
  myProfile: { ids: [], mayLoadMore: true },
  byProfileId: {},
};

export const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    setFeedSuggestedLiveIds: (
      state,
      action: PayloadAction<{
        ids: string[];
        isRefresh: boolean;
        fromMyTeam: boolean;
      }>,
    ) => {
      const { ids, fromMyTeam, isRefresh } = action.payload;
      const subBlock: keyof FeedSuggestedSubBlock = fromMyTeam
        ? 'myTeam'
        : 'nonMyTeam';
      const oldIds = state.suggested?.live[subBlock]?.ids || [];

      state.suggested.live[subBlock] = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore: ids.length === SHOWS_PER_PAGE,
      };
    },
    setFeedSuggestedRecordedIds: (
      state,
      action: PayloadAction<{
        ids: string[];
        isRefresh: boolean;
      }>,
    ) => {
      const { ids, isRefresh } = action.payload;
      const oldIds = state.suggested?.recorded?.ids || [];

      state.suggested.recorded = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore: ids.length === SHOWS_PER_PAGE,
      };
    },
    addCreatedShowToSuggestedIds: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      const oldIds = state.suggested?.live?.myTeam?.ids || [];

      state.suggested.live.myTeam.ids = Array.from(new Set([id, ...oldIds]));
    },
    removeLiveShowFromSuggestedIds: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      const { id } = action.payload;
      let isExists = false;

      const myTeamIdsArray = state.suggested.live?.myTeam?.ids;
      const myTeamIds = !myTeamIdsArray?.length
        ? []
        : myTeamIdsArray.filter((currentId) => {
            if (currentId === id) {
              isExists = true;
            }
            return currentId !== id;
          });

      const nonMyTeamIdsArray = state.suggested.live?.nonMyTeam?.ids;
      const nonMyTeamIds = !nonMyTeamIdsArray?.length
        ? []
        : nonMyTeamIdsArray.filter((currentId) => {
            if (currentId === id) {
              isExists = true;
            }
            return currentId !== id;
          });

      state.suggested.live.myTeam.ids = myTeamIds;
      state.suggested.live.nonMyTeam.ids = nonMyTeamIds;
      if (isExists) {
        const oldRecordedIds = state.suggested.recorded?.ids || [];
        state.suggested.recorded.ids = [id, ...oldRecordedIds];
      }
    },
    removeLiveShowFromFollowingIds: (
      state,
      action: PayloadAction<{ id: string }>,
    ) => {
      const { id } = action.payload;
      let isExists = false;

      const liveIdsArray = state.following.live?.ids;
      const liveIds = !liveIdsArray?.length
        ? []
        : liveIdsArray.filter((currentId) => {
            if (currentId === id) {
              isExists = true;
            }
            return currentId !== id;
          });

      state.following.live.ids = liveIds;
      if (isExists) {
        const oldRecordedIds = state.following.recorded?.ids || [];
        state.following.recorded.ids = [id, ...oldRecordedIds];
      }
    },
    setMyProfileRecordedShowsIds: (
      state,
      action: PayloadAction<{
        ids: string[];
        isRefresh: boolean;
      }>,
    ) => {
      const { ids, isRefresh } = action.payload;
      const oldIds = state.myProfile?.ids || [];

      state.myProfile.ids = mergeIds(oldIds, ids, isRefresh);
      state.myProfile.mayLoadMore = ids.length === SHOWS_PER_PAGE;
    },
    removeShowFromIds: (state, action: PayloadAction<string>) => {
      const id = action.payload;

      const myProfileIdsArray = state.myProfile.ids;
      const myProfileIds = !myProfileIdsArray?.length
        ? []
        : myProfileIdsArray.filter((currentId) => currentId !== id);

      const suggestedIds = !state.suggested?.recorded?.ids?.length
        ? []
        : [...state.suggested.recorded.ids].filter(
            (currentId) => currentId !== id,
          );
      const followingIds = !state.following.recorded?.ids?.length
        ? []
        : [...state.following.recorded.ids].filter(
            (currentId) => currentId !== id,
          );

      state.myProfile.ids = myProfileIds;
      state.suggested.recorded.ids = suggestedIds;
      state.following.recorded.ids = followingIds;
    },
    setRecordedShowsByProfileId: (
      state,
      action: PayloadAction<{
        userId: string;
        ids: string[];
        isRefresh: boolean;
      }>,
    ) => {
      const { userId, ids, isRefresh } = action.payload;
      const oldIds = state.byProfileId[userId]?.ids || [];
      state.byProfileId[userId] = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore: ids.length === SHOWS_PER_PAGE,
      };
    },
    setFeedSFollowingIds: (
      state,
      action: PayloadAction<{
        ids: string[];
        isRefresh: boolean;
        block: keyof FeedFollowingBlock;
      }>,
    ) => {
      const { ids, isRefresh, block } = action.payload;
      const oldIds = state.following[block]?.ids || [];

      state.following[block] = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore: ids.length === SHOWS_PER_PAGE,
      };
    },
    setScheduledShowsIds: (
      state,
      action: PayloadAction<{
        ids: string[];
        isRefresh: boolean;
      }>,
    ) => {
      const { ids, isRefresh } = action.payload;
      const oldIds = state.scheduled?.ids || [];

      state.scheduled = {
        ids: mergeIds(oldIds, ids, isRefresh),
        mayLoadMore: ids.length === SHOWS_PER_PAGE,
      };
    },
    addCreatedScheduledShowToFeedIds: (
      state,
      action: PayloadAction<string>,
    ) => {
      const id = action.payload;
      const oldIds = state.scheduled?.ids || [];
      state.scheduled.ids = Array.from(new Set([id, ...oldIds]));
    },
    removeCreatedScheduledShowToFeedIds: (
      state,
      action: PayloadAction<string>,
    ) => {
      const id = action.payload;
      const ids = !state.scheduled.ids?.length
        ? []
        : [...state.scheduled.ids].filter((currentId) => currentId !== id);

      state.scheduled.ids = ids;
    },
    resetFeedFollowingIds: (state) => {
      state.following = {
        live: initialPaginationState,
        recorded: initialPaginationState,
      };
    },
    resetFeedSuggestedIds: (state) => {
      state.suggested = {
        live: {
          myTeam: initialPaginationState,
          nonMyTeam: initialPaginationState,
        },
        recorded: initialPaginationState,
      };
    },
    resetFeedScheduledIds: (state) => {
      state.scheduled = initialPaginationState;
    },
  },
});

export const {
  setFeedSuggestedLiveIds,
  setFeedSuggestedRecordedIds,
  addCreatedShowToSuggestedIds,
  removeLiveShowFromSuggestedIds,
  removeLiveShowFromFollowingIds,
  setMyProfileRecordedShowsIds,
  removeShowFromIds,
  setRecordedShowsByProfileId,
  setFeedSFollowingIds,
  setScheduledShowsIds,
  addCreatedScheduledShowToFeedIds,
  removeCreatedScheduledShowToFeedIds,
  resetFeedFollowingIds,
  resetFeedSuggestedIds,
  resetFeedScheduledIds,
} = feedSlice.actions;

export default feedSlice.reducer;
